/**
 * Media Utilities
 * 
 * Functions for handling media files, images, and authenticated URLs
 */

import defaultProfileImage from "@/assets/images/domains/profile/avatars/default/user.png"
import defaultGroupImage from "@/assets/images/domains/groups/default/group-default-image.jpg"
import defaultEventCoverImage from "@/assets/images/icon/placeholder-event.jpg"
import { getApiBaseUrl } from '@/shared/config/api.config';
import {
  getAccessTokenToCookies,
  getAccessToken,
  isTokenExpired,
} from '@/shared/utils/tokenHelper'

/**
 * Get file URL from file ID
 * @param {string} fileId - The file ID
 * @returns {Promise<string|null>} File URL in /uploads/* format or null if not found
 */
export async function getFileUrlFromId(fileId) {
  if (!fileId) return null;

  const baseUrl = getApiBaseUrl();
  const isUseCookies = process.env.VUE_APP_USE_COOKIES === 'true';
  const token = isUseCookies ? getAccessTokenToCookies() : getAccessToken();

  if (!token) return null;

  try {
    const response = await fetch(`${baseUrl}/storage/file/${fileId}`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });

    if (!response.ok) return null;

    const data = await response.json();
    return data.url || null;
  } catch {
    return null;
  }
}

/**
 * Get signed URL for a file
 * @param {string} fileId - The file ID
 * @param {number} expiresIn - Expiration time in seconds (default: 3600)
 * @returns {Promise<string|null>} Signed URL or null
 * @throws {Error} If token is missing or API request fails
 */
export async function getSignedMediaUrl(fileId, expiresIn = 3600) {
  if (!fileId) return null;

  const baseUrl = getApiBaseUrl();
  const isUseCookies = process.env.VUE_APP_USE_COOKIES === 'true';
  const token = isUseCookies ? getAccessTokenToCookies() : getAccessToken();

  if (!token) {
    throw new Error('No access token found - user may need to re-authenticate');
  }

  try {
    const response = await fetch(`${baseUrl}/storage/file/${fileId}/signed-url?expiresIn=${expiresIn}`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });

    if (!response.ok) {
      // Fallback to getting /uploads/* URL from metadata
      const fileUrl = await getFileUrlFromId(fileId);
      if (fileUrl) {
        return getAuthenticatedImageUrl(fileUrl);
      }
      throw new Error(`Failed to get signed URL (HTTP ${response.status})`);
    }

    const data = await response.json();
    return data.signedUrl || null;
  } catch (error) {
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      throw new Error('Network error: Unable to reach server. Please check your connection.');
    }
    throw error;
  }
}

/**
 * Collapse accidental `/uploads/uploads/...` paths (backend sometimes double-prefixes).
 * @param {string} path - URL path (pathname) or path-like string
 * @returns {string}
 */
export function normalizeDuplicateUploadsPath(path) {
  if (!path || typeof path !== 'string') return path;
  let p = path.trim();
  while (p.includes('/uploads/uploads')) {
    p = p.replace(/\/uploads\/uploads\//g, '/uploads/').replace(/\/uploads\/uploads$/g, '/uploads');
  }
  return p;
}

/**
 * Build media URL using /uploads/ path (NOT /uploads-secure/).
 * Use for media subresources (video poster, thumbnails) where the
 * /uploads-secure/ cookie-based auth may fail on cross-origin or
 * subresource requests.
 * @param {string} url - Path from API (mediaUrl, file.url, thumbnailUrl, etc.) - relative or absolute
 * @returns {string} Full URL with /uploads/ path
 */
export function getUploadsMediaUrl(url) {
  if (!url || url.startsWith('blob:')) {
    return url || '';
  }
  const baseUrl = getApiBaseUrl().replace(/\/$/, '');
  let path = url;
  if (path.startsWith('http')) {
    try {
      const u = new URL(path);
      path = u.pathname + u.search + u.hash;
    } catch {
      return url;
    }
  }
  path = path.startsWith('/') ? path : `/${path}`;
  path = normalizeDuplicateUploadsPath(path);
  const pathUploads = path.replace(/^\/uploads-secure\//, '/uploads/');
  if (!pathUploads.startsWith('/uploads/')) {
    const normalized = normalizeFilePath(pathUploads.replace(/^\//, ''));
    return baseUrl + (normalized.startsWith('/') ? normalized : `/${normalized}`);
  }
  return baseUrl + pathUploads;
}

/**
 * Ensure a media URL uses backend streaming instead of redirect when possible.
 * Appends ?stream=true (or &stream=true) to uploads/uploads-secure URLs so the server
 * streams the file from S3 instead of redirecting to a pre-signed URL. This avoids
 * CORS errors on desktop when the redirect target (e.g. OSS) does not send CORS headers.
 * Use for audio/video that is loaded via fetch() or <audio>/<video> in JS.
 * @param {string} url - Media URL (full or relative)
 * @returns {string} URL with stream=true if it's an uploads URL
 */
export function ensureStreamingMediaUrl(url) {
  if (!url || typeof url !== 'string') return url;
  if (url.includes('stream=true')) return url;
  const isUploads = url.includes('uploads-secure') || url.includes('/uploads/');
  if (!isUploads) return url;
  return url.includes('?') ? `${url}&stream=true` : `${url}?stream=true`;
}

/**
 * Get authentication token
 * @returns {string} Token or empty string
 */
function getAuthToken() {
  try {
    const isUseCookies = process.env.VUE_APP_USE_COOKIES === 'true';
    return isUseCookies ? getAccessTokenToCookies() : getAccessToken();
  } catch {
    return '';
  }
}

/**
 * Refresh token if expired (fire and forget)
 */
function refreshTokenIfNeeded() {
  try {
    const token = getAuthToken();
    if (token && isTokenExpired()) {
      const isUseCookies = process.env.VUE_APP_USE_COOKIES === 'true';
      (async () => {
        try {
          if (isUseCookies) {
            const { refreshCookies } = await import('@/domains/auth/services/auth.service');
            await refreshCookies();
            const { pushRefreshedTokenToSocket } = await import('@/shared/services/webSocket');
            const { getAccessTokenToCookies } = await import('@/shared/utils/tokenHelper');
            pushRefreshedTokenToSocket(getAccessTokenToCookies());
          } else {
            const { refreshToken } = await import('@/domains/auth/services/auth.service');
            const result = await refreshToken();
            if (result.success) {
              const { pushRefreshedTokenToSocket } = await import('@/shared/services/webSocket');
              const { getAccessToken } = await import('@/shared/utils/tokenHelper');
              pushRefreshedTokenToSocket(getAccessToken());
            }
          }
        } catch {
          // Refresh failed - will be handled by API interceptor
        }
      })();
    }
  } catch {
    // Token check failed - continue
  }
}

/**
 * Normalize relative path to /uploads/ format if needed
 * @param {string} path - Path to normalize
 * @returns {string} Normalized path
 */
function normalizeFilePath(path) {
  const filePathPatterns = [
    /^(posts|profiles|forums|stories|covers|groups|chat_media|policies|listings|albums|pages)\//,
    /^images\/(?!tree|theme-modern|ui|theme|icons|sprites)/,
  ];

  const isFilePath = filePathPatterns.some(pattern => pattern.test(path));
  if (isFilePath && !path.startsWith('/uploads/')) {
    return '/uploads/' + path.replace(/^\/+/, '');
  }
  return path;
}

/**
 * Build full URL from relative path
 * @param {string} path - Relative path
 * @param {string|null} customBaseUrl - Optional custom base URL
 * @returns {string} Full URL
 */
function buildFullUrlFromPath(path, customBaseUrl) {
  if (path.startsWith('/uploads/')) {
    return `${globalThis.location.origin}/apimain${path}`;
  }

  if (customBaseUrl) {
    return customBaseUrl.replace(/\/$/, '') + path;
  }

  const baseUrl = getApiBaseUrl().replace('/apimain', '/api/0.0.1/uploads');
  return baseUrl.replace(/\/$/, '') + path;
}

/**
 * Process full URL (http:// or https://)
 * @param {string} imageUrl - Full URL
 * @param {string|null} customBaseUrl - Optional custom base URL
 * @returns {string} Processed URL
 */
function processFullUrl(imageUrl, customBaseUrl) {
  try {
    const urlObj = new URL(imageUrl);

    // Convert HTTP to HTTPS if page is HTTPS
    if (urlObj.protocol === 'http:' && globalThis.location?.protocol === 'https:') {
      urlObj.protocol = 'https:';
      imageUrl = urlObj.toString();
    }

    // Rebuild with custom base if provided
    if (customBaseUrl && !imageUrl.startsWith(customBaseUrl)) {
      const urlObj2 = new URL(imageUrl);
      const path = urlObj2.pathname + urlObj2.search + urlObj2.hash;
      return customBaseUrl.replace(/\/$/, '') + path;
    }

    return imageUrl;
  } catch {
    return imageUrl;
  }
}

/**
 * Get authenticated image URL
 * The single, definitive function for constructing a full, authenticated media URL.
 * 
 * @param {string} imageUrl - The image path from the API (can be relative or absolute)
 * @param {string|null} customBaseUrl - Optional custom base URL (e.g., for market service)
 * @returns {string|null} The final, browser-usable URL with token.
 */
export function getAuthenticatedImageUrl(imageUrl, customBaseUrl = null) {
  if (!imageUrl || imageUrl.startsWith('blob:')) {
    return imageUrl || null;
  }

  const token = (process.env.VUE_APP_USE_COOKIES === 'true')
    ? getAccessTokenToCookies()
    : getAccessToken();

  // Determine the correct base URL. Use the custom one if provided, otherwise the main API URL.
  const baseUrl = (customBaseUrl || getApiBaseUrl()).replace(/\/$/, ''); // Remove trailing slash for clean join

  let finalUrl;

  // Case 1: The imageUrl is already an absolute URL.
  if (imageUrl.startsWith('http')) {
    try {
      const u = new URL(imageUrl);
      u.pathname = normalizeDuplicateUploadsPath(u.pathname);
      finalUrl = u.toString();
    } catch {
      finalUrl = imageUrl;
    }
  }
  // Case 2: The imageUrl is a relative path.
  else {
    const path = normalizeDuplicateUploadsPath(
      imageUrl.startsWith('/') ? imageUrl : `/${imageUrl}`,
    );
    finalUrl = `${baseUrl}${path}`;
  }

  // Convert /uploads/ to /uploads-secure/ (skip paths already secured)
  if (finalUrl.includes('/uploads/') && !finalUrl.includes('/uploads-secure/')) {
    return finalUrl.replace('/uploads/', '/uploads-secure/');
  }

  /* 
   * LEGACY JWT IMPLEMENTATION - KEPT FOR REFERENCE BUT DISABLED
   * 
  // Always append the current token, ensuring no old tokens are left in the URL.
  if (token) {
    try {
      const urlObj = new URL(finalUrl);
      urlObj.searchParams.set('token', token);
      return urlObj.toString();
    } catch (e) {
      // Fallback for malformed URLs
      const cleanUrl = finalUrl.split('?')[0];
      return `${cleanUrl}?token=${encodeURIComponent(token)}`;
    }
  }
  */

  return finalUrl;
}


/**
 * Display image with fallback
 * @param {string} url - Image URL
 * @param {string} useCase - Use case: 'profile', 'group', 'highlight', 'market'
 * @returns {string} Authenticated image URL or default fallback
 */
export function displayImage(url, useCase = 'profile') {
  if (!url) {
    switch (useCase) {
      case "profile":
        return defaultProfileImage;
      case "group":
      case "highlight":
      case 'market':
        return defaultGroupImage;
      case "event":
        return defaultEventCoverImage;
      default:
        return defaultProfileImage;
    }
  }

  if (useCase === 'market') {
    const marketBaseUrl = `${globalThis.location.origin}/apimarket/api`;
    return getAuthenticatedImageUrl(url, marketBaseUrl) || defaultProfileImage;
  }

  if (useCase === "event") {
    return getAuthenticatedImageUrl(url) || defaultEventCoverImage;
  }

  return getAuthenticatedImageUrl(url) || defaultProfileImage;
}

/** Poster/thumbnail URL for feed & gallery video (authenticated /uploads-secure/). */
export function getVideoPosterUrl(media) {
  if (!media) return null;
  const thumbnail =
    media?.file?.thumbnailUrl ||
    media?.thumbnailUrl ||
    media?.playback?.spriteUrl ||
    null;
  return thumbnail ? displayImage(thumbnail) : null;
}

/** Primary playback path from API (HLS manifest or MP4). */
export function getVideoPlaybackPath(media) {
  if (!media) return '';
  return (
    media?.playback?.url ||
    media?.preferredVideoUrl ||
    media?.file?.url ||
    media?.mediaUrl ||
    ''
  );
}

export function isHlsVideoMedia(media) {
  if (!media) return false;
  const playback = media?.playback;
  const path = getVideoPlaybackPath(media);
  return playback?.type === 'HLS' || (!!path && path.includes('.m3u8'));
}

/** Browser-ready stream URL: HLS manifests use /uploads/ (Reels parity); MP4 uses authenticated URL. */
export function getVideoStreamUrl(media) {
  const path = getVideoPlaybackPath(media);
  if (!path) return '';
  if (isHlsVideoMedia(media)) {
    return getUploadsMediaUrl(path);
  }
  return displayImage(path);
}

/**
 * Handle image load errors and retry with fresh token
 * @param {Event} event - The error event from the img element
 * @param {string} originalUrl - The original image URL (without token)
 * @param {number} maxRetries - Maximum number of retries (default: 1)
 * @returns {Promise<void>}
 */
export async function handleImageError(event, originalUrl, maxRetries = 1) {
  const img = event.target;
  const retryCount = Number.parseInt(img.dataset.retryCount || '0', 10);

  if (retryCount >= maxRetries) {
    img.src = defaultProfileImage;
    return;
  }

  const isUseCookies = process.env.VUE_APP_USE_COOKIES === 'true';

  try {
    if (isUseCookies) {
      const { refreshCookies } = await import('@/domains/auth/services/auth.service');
      await refreshCookies();
      const { pushRefreshedTokenToSocket } = await import('@/shared/services/webSocket');
      const { getAccessTokenToCookies } = await import('@/shared/utils/tokenHelper');
      pushRefreshedTokenToSocket(getAccessTokenToCookies());
    } else {
      const { refreshToken } = await import('@/domains/auth/services/auth.service');
      const result = await refreshToken();
      if (result.success) {
        const { pushRefreshedTokenToSocket } = await import('@/shared/services/webSocket');
        const { getAccessToken } = await import('@/shared/utils/tokenHelper');
        pushRefreshedTokenToSocket(getAccessToken());
      }
    }

    await new Promise(resolve => setTimeout(resolve, 100));

    img.dataset.retryCount = String(retryCount + 1);
    const freshUrl = getAuthenticatedImageUrl(originalUrl);
    img.src = freshUrl || defaultProfileImage;
  } catch {
    img.src = defaultProfileImage;
  }
}
